/**
 * Blocked Senders View (Report Phase B)
 *
 * Admin surface for the per-tenant blocked-sender list. Rows come from
 * two sources:
 *   - Auto-inserted by the user "Report" toolbar action (source=user_report)
 *   - Manually added by an admin via this view (source=admin)
 *
 * Unblock is always a one-click delete. The backend removes the row and
 * the next inbound from the sender will no longer auto-file to Junk.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  NoSymbolIcon,
  PlusIcon,
  TrashIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import EdgeMailAdminAPI, {
  type BlockedSender,
  type CreateBlockedSenderInput,
} from '../../services/EdgeMailAdminAPI';

function formatRelative(iso: string): string {
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.round(sec / 60)} min ago`;
  if (sec < 86400) return `${Math.round(sec / 3600)} h ago`;
  return d.toLocaleDateString();
}

const REASON_STYLES: Record<BlockedSender['reason'], string> = {
  spam: 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300',
  phishing: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
  abuse: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
  manual: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300',
};

const SOURCE_LABEL: Record<BlockedSender['source'], string> = {
  user_report: 'User report',
  admin: 'Admin',
  auto: 'Auto',
};

const BlockedSendersView: React.FC = () => {
  const [rows, setRows] = useState<BlockedSender[] | null>(null);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await EdgeMailAdminAPI.listBlockedSenders();
      setRows(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load blocked senders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.sender_address && r.sender_address.includes(q)) ||
      (r.sender_domain && r.sender_domain.includes(q)) ||
      r.reason.includes(q) ||
      r.source.includes(q),
    );
  }, [rows, filter]);

  const handleUnblock = useCallback(
    async (row: BlockedSender) => {
      // Optimistic removal — rollback on error.
      const previous = rows ?? [];
      setRows(previous.filter((r) => r.blocked_sender_id !== row.blocked_sender_id));
      try {
        await EdgeMailAdminAPI.deleteBlockedSender(row.blocked_sender_id);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to unblock');
        setRows(previous);
      }
    },
    [rows],
  );

  const handleAdd = useCallback(
    async (input: CreateBlockedSenderInput) => {
      setError(null);
      try {
        const created = await EdgeMailAdminAPI.createBlockedSender(input);
        setRows((prev) => [created, ...(prev ?? [])]);
        setAddOpen(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to add block');
      }
    },
    [],
  );

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <NoSymbolIcon className="w-5 h-5 text-red-500" />
            Blocked Senders
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Addresses and domains whose mail is auto-filed to Junk before delivery. Populated by user "Report" actions and admin additions.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => void refresh()}
            className="p-2 text-gray-500 hover:text-gray-900 dark:hover:text-gray-100 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
            title="Refresh"
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setAddOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-blue-600 hover:bg-blue-700 text-white"
          >
            <PlusIcon className="w-4 h-4" />
            Add block
          </button>
        </div>
      </div>

      {/* Filter */}
      <div className="relative max-w-sm">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by address, domain, reason…"
          className="w-full pl-9 pr-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400"
        />
      </div>

      {error && (
        <div className="px-3 py-2 rounded bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      )}

      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-900">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800/50">
            <tr className="text-left">
              <th className="px-4 py-2 font-medium text-gray-600 dark:text-gray-400">Target</th>
              <th className="px-4 py-2 font-medium text-gray-600 dark:text-gray-400">Scope</th>
              <th className="px-4 py-2 font-medium text-gray-600 dark:text-gray-400">Reason</th>
              <th className="px-4 py-2 font-medium text-gray-600 dark:text-gray-400">Source</th>
              <th className="px-4 py-2 font-medium text-gray-600 dark:text-gray-400">Added</th>
              <th className="px-4 py-2 w-10" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {filtered.length === 0 && !loading && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                  {rows === null ? 'Loading…' : filter ? 'No blocks match your filter.' : 'No blocked senders yet.'}
                </td>
              </tr>
            )}
            {filtered.map((row) => (
              <tr key={row.blocked_sender_id} className="hover:bg-gray-50 dark:hover:bg-gray-800/30">
                <td className="px-4 py-2 font-mono text-xs text-gray-900 dark:text-gray-100">
                  {row.sender_address ?? row.sender_domain}
                </td>
                <td className="px-4 py-2 text-gray-700 dark:text-gray-300">
                  {row.mailbox_id ? 'Mailbox' : 'Tenant-wide'}
                </td>
                <td className="px-4 py-2">
                  <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded ${REASON_STYLES[row.reason]}`}>
                    {row.reason}
                  </span>
                </td>
                <td className="px-4 py-2 text-gray-600 dark:text-gray-400">{SOURCE_LABEL[row.source]}</td>
                <td className="px-4 py-2 text-gray-500 dark:text-gray-400">{formatRelative(row.created_at)}</td>
                <td className="px-4 py-2">
                  <button
                    onClick={() => void handleUnblock(row)}
                    title="Unblock"
                    className="p-1 text-gray-400 hover:text-red-500 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {addOpen && <AddBlockModal onCancel={() => setAddOpen(false)} onSubmit={handleAdd} />}
    </div>
  );
};

const AddBlockModal: React.FC<{
  onCancel: () => void;
  onSubmit: (input: CreateBlockedSenderInput) => Promise<void> | void;
}> = ({ onCancel, onSubmit }) => {
  const [mode, setMode] = useState<'address' | 'domain'>('address');
  const [value, setValue] = useState('');
  const [reason, setReason] = useState<CreateBlockedSenderInput['reason']>('manual');
  const [submitting, setSubmitting] = useState(false);

  const disabled = !value.trim() || submitting;

  const submit = async () => {
    if (disabled) return;
    setSubmitting(true);
    try {
      await onSubmit({
        reason,
        ...(mode === 'address'
          ? { sender_address: value.trim().toLowerCase() }
          : { sender_domain: value.trim().toLowerCase() }),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-md rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-xl p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Block sender
        </h3>

        <div className="flex gap-2 text-sm">
          {(['address', 'domain'] as const).map((opt) => (
            <button
              key={opt}
              onClick={() => setMode(opt)}
              className={`px-3 py-1 rounded border ${
                mode === opt
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                  : 'border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300'
              }`}
            >
              {opt === 'address' ? 'Specific address' : 'Whole domain'}
            </button>
          ))}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
            {mode === 'address' ? 'Email address' : 'Domain (e.g. example.com)'}
          </label>
          <input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={mode === 'address' ? 'sender@example.com' : 'example.com'}
            className="w-full px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            autoFocus
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
            Reason
          </label>
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value as CreateBlockedSenderInput['reason'])}
            className="w-full px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            <option value="manual">Manual</option>
            <option value="spam">Spam</option>
            <option value="phishing">Phishing</option>
            <option value="abuse">Abuse</option>
          </select>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            onClick={onCancel}
            className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            Cancel
          </button>
          <button
            onClick={() => void submit()}
            disabled={disabled}
            className="px-3 py-1.5 text-sm rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white"
          >
            {submitting ? 'Blocking…' : 'Block sender'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BlockedSendersView;
